/**
 * LunkrBytes Hero Section JavaScript
 *
 * Features:
 * - Navigation expand/collapse
 * - Parallax scrolling effects
 * - Water ripple animation
 * - Accessibility (keyboard navigation, ARIA)
 * - Performance optimizations
 */

(function () {
  'use strict';

  // Feature detection
  const supportsIntersectionObserver = 'IntersectionObserver' in window;
  const supportsRequestAnimationFrame = 'requestAnimationFrame' in window;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Initialize hero section
   */
  function initHeroSection() {
    const heroSection = document.querySelector('.hero-lunkrbytes');
    if (!heroSection) return;

    // Get settings from data attributes
    const settings = {
      enableParallax: heroSection.dataset.enableParallax === 'true',
      enableWater: heroSection.dataset.enableWater === 'true',
      glowIntensity: parseInt(heroSection.dataset.glowIntensity) || 50
    };

    // Initialize features
    initNavigation(heroSection);
    initEntranceSequence(heroSection);

    // Apply glow intensity
    applyGlowIntensity(heroSection, settings.glowIntensity);
  }

  /**
   * Initialize navigation interactions
   * @param {HTMLElement} heroSection - Hero section element
   */
  function initNavigation(heroSection) {
    const navItems = heroSection.querySelectorAll('.hero-nav-item__link--expandable');

    navItems.forEach(item => {
      item.addEventListener('click', handleNavItemClick);
      item.addEventListener('keydown', handleNavItemKeydown);
    });
  }

  /**
   * Handle navigation item click
   * @param {Event} e - Click event
   */
  function handleNavItemClick(e) {
    const link = e.currentTarget;
    const hasSubmenu = link.classList.contains('hero-nav-item__link--expandable');

    if (hasSubmenu) {
      e.preventDefault();
      toggleSubmenu(link);
    }
  }

  /**
   * Handle navigation item keyboard interaction
   * @param {KeyboardEvent} e - Keyboard event
   */
  function handleNavItemKeydown(e) {
    const link = e.currentTarget;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        toggleSubmenu(link);
        break;
      case 'Escape':
        closeSubmenu(link);
        break;
    }
  }

  /**
   * Toggle submenu visibility
   * @param {HTMLElement} link - Navigation link element
   */
  function toggleSubmenu(link) {
    const isExpanded = link.getAttribute('aria-expanded') === 'true';
    const submenuId = link.getAttribute('aria-controls');
    const submenu = document.getElementById(submenuId);

    if (!submenu) return;

    // Update ARIA
    link.setAttribute('aria-expanded', !isExpanded);

    // Toggle submenu
    if (isExpanded) {
      submenu.hidden = true;
    } else {
      submenu.hidden = false;
      // Close other open submenus
      closeOtherSubmenus(link);
    }
  }

  /**
   * Close submenu
   * @param {HTMLElement} link - Navigation link element
   */
  function closeSubmenu(link) {
    const submenuId = link.getAttribute('aria-controls');
    const submenu = document.getElementById(submenuId);

    if (!submenu) return;

    link.setAttribute('aria-expanded', 'false');
    submenu.hidden = true;
  }

  /**
   * Close other open submenus
   * @param {HTMLElement} currentLink - Current navigation link
   */
  function closeOtherSubmenus(currentLink) {
    const allExpandableLinks = document.querySelectorAll('.hero-nav-item__link--expandable');

    allExpandableLinks.forEach(link => {
      if (link !== currentLink && link.getAttribute('aria-expanded') === 'true') {
        closeSubmenu(link);
      }
    });
  }

  /**
   * Initialize entrance sequence for a "powering up" effect
   * @param {HTMLElement} heroSection - Hero section element
   */
  function initEntranceSequence(heroSection) {
    if (prefersReducedMotion) return;

    // Add boot class to start the sequence
    setTimeout(() => {
      heroSection.classList.add('hero-lunkrbytes--boot');
    }, 100);

    // Staggered reveal for sidebar navigation items
    const navItems = heroSection.querySelectorAll('.hero-nav-item');
    navItems.forEach((item, index) => {
      item.style.setProperty('--item-index', index);
    });

    // Staggered reveal for feature cards
    const featureCards = heroSection.querySelectorAll('.hero-feature-card');
    featureCards.forEach((card, index) => {
      card.style.setProperty('--card-index', index);
    });
  }

  /**
   * Apply glow intensity based on settings
   * @param {HTMLElement} heroSection - Hero section element
   * @param {number} intensity - Glow intensity (0-100)
   */
  function applyGlowIntensity(heroSection, intensity) {
    const scale = intensity / 50; // 50 is default, produces 1.0 scale
    heroSection.style.setProperty('--glow-scale', scale);
  }

  /**
   * Initialize on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeroSection);
  } else {
    initHeroSection();
  }

  // Handle Shopify Theme Editor events
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.hero-lunkrbytes')) {
      initHeroSection();
    }
  });

  /**
   * Cleanup function for single-page applications
   */
  window.lunkrbytesHeroCleanup = function () {
    // Remove event listeners if needed
    const navItems = document.querySelectorAll('.hero-nav-item__link--expandable');
    navItems.forEach(item => {
      item.removeEventListener('click', handleNavItemClick);
      item.removeEventListener('keydown', handleNavItemKeydown);
    });
  };

})();
